import { Grid, useMediaQuery, useTheme, Box, Fade } from "@mui/material";
import PageTitle from "../../../../utils/PageTitle";
import {
  selectExistingOrdersWithoutCancelled,
  selectRiskAssessments
} from "../../../../store/reducers/order/OrderInformationSlice";
import { useSelector } from "react-redux";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import ResultsSlider from "components/v3/pages/TestResultsPage/ResultsSlider";
import { selectUserPersonalInformation } from "store/reducers/user/UserAccountSlice";
import { useHistory } from "react-router-dom";
import WhatsNextBox from "components/v2/Pages/TestResultsPage/WhatsNextBox";
import ResultsSlidesPageStyles from "./ResultsSlidesPage.styles";
import TestResultsDNA from "assets/images/test-results-dna-strand.svg";
import { useState, useRef } from "react";
import { composeSlides } from "./slides";
import { PATHS } from "constants/pathConstants";
import { setHasNewResultsToView } from "store/reducers/user/UserAccountSlice";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import {
  trackResultsReviewCompleted,
  trackResultsReviewStarted
} from "analytics";
interface ResultsSlidesPageProps {
  slideIds?: string[];
}

const ResultsSlidesPage = ({ slideIds }: ResultsSlidesPageProps) => {
  const theme = useTheme();
  const screenWidthIsMdOrSmaller = useMediaQuery(theme.breakpoints.down("md"));
  const user = useSelector(selectUserPersonalInformation);
  const history = useHistory();
  const classes = ResultsSlidesPageStyles();
  const [activeSlide, setActiveSlide] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0); // used by slider via handleStepChange
  const [fadeIn, setFadeIn] = useState(true);
  const sliderRef = useRef<{ handleNext: () => void }>(null);
  PageTitle("Results slides");
  const orders = useSelector(selectExistingOrdersWithoutCancelled);
  const ordersWithDocs = orders.filter((order: any) => {
    return order?.documentIds?.length > 0 && order?.glTestType === "GHS";
  });
  const testResultsUrl = `${PATHS.TEST_RESULTS_PAGE}/${ordersWithDocs[0].glOrderId}`;

  const riskAssessment = useSelector(selectRiskAssessments);
  const dispatch = useDispatch();

  useEffect(() => {
    trackResultsReviewStarted("main");
    return () => {
      dispatch(setHasNewResultsToView(false));
    };
  }, [dispatch]);

  const handleSlideChange = (slideId: string) => {
    setFadeIn(false);
    setTimeout(() => {
      setActiveSlide(slideId);
      setFadeIn(true);
    }, 150);
  };

  const slides = composeSlides(
    user,
    riskAssessment,
    history,
    classes,
    testResultsUrl,
    slideIds || [
      "login",
      "gene-count",
      "ready-to-view-results-question",
      "results"
    ],
    () => {
      sliderRef.current?.handleNext();
    }
  );

  const handleStepChange = (step: number) => {
    setActiveStep(step);
    if (step === slides.length - 1) {
      trackResultsReviewCompleted({
        slideSet: "main",
        slidesViewedCount: slides.length
      });
    }
  };

  const containedSlides = () => {
    if (screenWidthIsMdOrSmaller) {
      return (
        <ResultsSlider
          ref={sliderRef}
          slides={slides}
          initialSlide={activeStep}
          onSlideChange={(slideId) => {
            handleSlideChange(slideId);
          }}
          onStepChange={handleStepChange}
        />
      );
    } else {
      return (
        <Grid
          container
          direction="row"
          justifyContent="space-around"
          className={classes.wideModeContainer}
        >
          <Grid item md={7} lg={5} className={classes.wideModeSlidesContainer}>
            <ResultsSlider
              ref={sliderRef}
              slides={slides}
              initialSlide={activeStep}
              onSlideChange={(slideId) => {
                handleSlideChange(slideId);
              }}
              onStepChange={handleStepChange}
            />
          </Grid>
          <Grid
            item
            md={4}
            lg={5}
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%"
            }}
          >
            <Fade in={fadeIn} timeout={150}>
              <Box sx={{ width: "100%" }}>
                {activeSlide === "more-info-outro-slide" ? (
                  <WhatsNextBox smallMode={false} />
                ) : (
                  <img
                    src={TestResultsDNA}
                    style={{
                      width: "100%",
                      maxHeight: "400px",
                      objectFit: "contain"
                    }}
                    alt="Test Results DNA"
                  />
                )}
              </Box>
            </Fade>
          </Grid>
        </Grid>
      );
    }
  };

  return (
    <GradientFramedLayout
      showHeader={true}
      showFooter={false}
      showNav={false}
      makeLogoClickable={false}
    >
      <Grid container className={classes.container}>
        {containedSlides()}
      </Grid>
    </GradientFramedLayout>
  );
};

export default ResultsSlidesPage;
